import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { portfolioWorks } from '../data/mock';

const bgColorMap = {
  'light-pink': 'bg-pink-100',
  'light-yellow': 'bg-yellow-100',
  'grey': 'bg-slate-200',
  'mid-purple': 'bg-purple-200',
  'mid-blue': 'bg-cyan-200',
  'mid-orange': 'bg-orange-200',
};

const textColorMap = {
  'light-pink': 'text-gray-800',
  'light-yellow': 'text-gray-800',
  'grey': 'text-gray-800',
  'mid-purple': 'text-gray-800',
  'mid-blue': 'text-gray-800',
  'mid-orange': 'text-gray-800',
};

const PortfolioSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <section id="work" className="py-24 lg:py-32 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="service-button bg-purple-500/20 text-purple-400 border-purple-500/30 mb-4 inline-block">
            Our Work
          </span>
          <h2 className="section-heading text-white mt-4">
            Projects That
            <span className="text-purple-400"> Speak</span>
          </h2>
          <p className="body-large text-white/60 mt-4 max-w-2xl">
            Real results for real businesses. Check out some of our recent client work.
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {portfolioWorks.map((work) => (
            <motion.a
              key={work.id}
              href={work.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={itemVariants}
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              className={`project-card ${bgColorMap[work.bgColor]} ${textColorMap[work.bgColor]} rounded-xl overflow-hidden min-h-[320px] flex flex-col group cursor-pointer`}
            >
              {/* Logo Section */}
              <div className="h-32 flex items-center justify-center p-4 bg-white/50">
                {work.logo ? (
                  <img 
                    src={work.logo} 
                    alt={`${work.title} logo`}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <div className="text-2xl font-bold text-gray-400">{work.title.charAt(0)}</div>
                )}
              </div>

              {/* Content Section */}
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="card-heading group-hover:underline">{work.title}</h3>
                  <ExternalLink
                    size={18}
                    className="opacity-50 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2"
                  />
                </div>
                <p className="body-small opacity-70 mb-4">{work.description}</p>

                <div className="flex flex-wrap gap-2 mt-auto">
                  {work.categories.map((category, idx) => (
                    <span
                      key={idx}
                      className="text-xs font-mono tracking-wider px-3 py-1 rounded-full bg-black/10 text-current"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;
